import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { familiesApi, Family } from '../api/families';
import {
  chatApi,
  ChatHistoryTurn,
  FocusModeConfig,
  MedicalChatCitation,
  MedicalChatResponse,
  MemberCandidate,
} from '../api/chat';
import { memberDisplayName } from '../utils/memberDisplay';
import { useIsAdvanced } from '../utils/viewMode';
import remarkGfm from 'remark-gfm';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import type { Components } from 'react-markdown';

type Msg = {
  role: 'user' | 'assistant';
  text: string;
  /** Model chain-of-thought extracted server-side; shown in a distinct UI block. */
  reasoning?: string | null;
  citations?: MedicalChatCitation[];
  meta?: string;
  /** Clickable choices when server asks which member (ambiguous match). */
  memberPick?: MemberCandidate[];
};

/** Live agentic stream (steps + thinking + draft answer) before the final assistant message is committed. */
type AgenticStreamState = {
  phaseLabel: string;
  liveThinking: string;
  liveAnswer: string;
};

const ChatBubbleIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4 20-7z" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="11" x2="12" y2="16" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const CloseIconSmall = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

/** Tiny chevron used by the streaming "thinking" disclosure. Rotates 90deg
 *  via CSS when the parent has `.is-expanded`, no React state needed. */
const ChevronRightIcon = () => (
  <svg
    className="medical-chat-stream-thinking-chev"
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="m9 6 6 6-6 6" />
  </svg>
);

/** Translate the raw agentic phase labels (`Apel retrieve_chunks: …`,
 *  `Intent analizat`, `tool_result: …`) into calm, user-facing copy so
 *  the streaming panel reads like a status whisper, not a debug log.
 *  Returns the original label as a safe fallback. */
const friendlyPhaseLabel = (raw: string): string => {
  const s = (raw || '').trim();
  if (!s) return 'Pregătesc răspunsul…';
  const lower = s.toLowerCase();
  if (lower.startsWith('intent') || lower.includes('intenți')) return 'Înțeleg întrebarea…';
  if (lower.includes('retrieve_chunks')) return 'Caut în documente…';
  if (lower.includes('metadata_search')) return 'Caut metadate…';
  if (lower.includes('get_classification_details')) return 'Verific detaliile…';
  if (lower.startsWith('tool_result') || lower.startsWith('rezultat')) return 'Procesez rezultatele…';
  if (lower.startsWith('apel ')) return 'Pregătesc o căutare…';
  return s;
};

/** Three-dot typing indicator. Staggered keyframes live in App.css so
 *  the component stays a pure presentation primitive (no JS animation). */
const TypingDots = () => (
  <span className="medical-chat-stream-dots" aria-hidden>
    <i />
    <i />
    <i />
  </span>
);

const MOBILE_FULLSCREEN_MQ = '(max-width: 768px)';

const MedicalChatBubble: React.FC = () => {
  const isAdvanced = useIsAdvanced();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mobileModal, setMobileModal] = useState(false);
  /** Header info-tooltip toggle. Desktop shows it on hover/focus via CSS;
   *  on touch devices we tap the icon to toggle and auto-dismiss on
   *  outside tap or any scroll (the CSS hover-state never fires there). */
  const [infoOpen, setInfoOpen] = useState(false);

  // Open-chat hook: any page can fire `edosar:open-chat` and the
  // bubble pops open. Lets the Dashboard expose an "Open chat" hero
  // CTA without us lifting the bubble's open state up the tree.
  useEffect(() => {
    const onOpenChat = () => setOpen(true);
    window.addEventListener('edosar:open-chat', onOpenChat);
    return () => window.removeEventListener('edosar:open-chat', onOpenChat);
  }, []);
  const [families, setFamilies] = useState<Family[]>([]);
  /** Sticky member for this chat (server returns resolved_member_id each turn). */
  const [sessionMemberId, setSessionMemberId] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Msg[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFamilies, setLoadingFamilies] = useState(true);
  const [error, setError] = useState('');
  /** Chat mode is fixed to the deeper agentic pipeline — the standard branch
   *  is no longer reachable from the UI. The legacy `standard | agentic` union
   *  is preserved so the API payload type doesn't change. */
  const chatMode = 'agentic' as const;
  /** Active focus key. Free-form string so renaming a focus in the admin
   *  UI doesn't require a frontend redeploy; the list of valid values is
   *  fetched at mount time from `/api/chat/focus-modes`. */
  const [focusMode, setFocusMode] = useState<string>('');
  const [focusModes, setFocusModes] = useState<FocusModeConfig[]>([]);
  const [agenticStream, setAgenticStream] = useState<AgenticStreamState | null>(null);
  /** "Vezi gândirea" disclosure for the streaming panel. Resets between
   *  turns so each new stream starts collapsed; while a stream is in
   *  flight the user's expand choice is preserved (we never auto-close). */
  const [thinkingExpanded, setThinkingExpanded] = useState(false);
  /** One in-flight agentic HTTP stream; aborted when starting a new turn or closing the panel. */
  const agenticStreamAbortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const loadFamilies = useCallback(async () => {
    try {
      const data = await familiesApi.getFamilies();
      setFamilies(data);
      return data;
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Eroare la incarcarea familiilor');
      return [];
    }
  }, []);

  useEffect(() => {
    (async () => {
      await loadFamilies();
      setLoadingFamilies(false);
    })();
  }, [loadFamilies]);

  // Focus-mode dropdown is admin-managed: fetch the active list once at
  // mount instead of hardcoding `labs` / `imaging` / `medication`. We
  // fall back to a no-focus state when the fetch fails (the user can
  // still chat — focus is purely a retrieval hint).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await chatApi.listFocusModes();
        if (cancelled) return;
        setFocusModes(list);
        // If the previously-selected focus is no longer in the registry
        // (admin removed it), clear it so we don't send an unknown key.
        if (list.length > 0 && focusMode && !list.some((m) => m.key === focusMode)) {
          setFocusMode('');
        }
      } catch {
        // Silent: dropdown stays at "Automat" / "Toate documentele".
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run-once on mount; we don't want focus-mode changes to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routeMemberId = matchPath({ path: '/members/:memberId', end: true }, location.pathname)?.params.memberId;
  const routeDocumentId = matchPath({ path: '/documents/:documentId', end: true }, location.pathname)?.params
    .documentId;
  const [useCurrentDocument, setUseCurrentDocument] = useState(false);

  useEffect(() => {
    if (!routeMemberId || families.length === 0) return;
    const id = Number(routeMemberId);
    if (!Number.isFinite(id)) return;
    const exists = families.some((f) => f.members.some((m) => m.id === id));
    if (exists) {
      // Switching the UI to a different member page should not carry the old chat transcript,
      // because it can contaminate the LLM with the previous member's context.
      agenticStreamAbortRef.current?.abort();
      agenticStreamAbortRef.current = null;
      setAgenticStream(null);
      setMessages([]);
      setError('');
      setSessionMemberId(id);
    }
  }, [routeMemberId, families]);

  useEffect(() => {
    // If the user is on a document page, default to chatting with that document.
    if (!routeDocumentId) {
      setUseCurrentDocument(false);
      return;
    }
    const id = Number(routeDocumentId);
    if (!Number.isFinite(id) || id <= 0) {
      setUseCurrentDocument(false);
      return;
    }
    setUseCurrentDocument(true);
  }, [routeDocumentId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, open, agenticStream]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_FULLSCREEN_MQ);
    const sync = () => setMobileModal(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) {
      agenticStreamAbortRef.current?.abort();
      agenticStreamAbortRef.current = null;
      setAgenticStream(null);
    }
  }, [open]);

  // Reset the "thinking" disclosure once the live stream ends so the next
  // turn starts collapsed. Triggered when the panel unmounts (agenticStream
  // becomes null after a `done`/`error` event or a new turn starts fresh).
  useEffect(() => {
    if (!agenticStream) setThinkingExpanded(false);
  }, [agenticStream]);

  useEffect(() => {
    if (!open || !mobileModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, mobileModal]);

  // Auto-dismiss the header info tooltip when the user taps outside it or
  // scrolls anywhere (covers touch devices that don't fire CSS :hover).
  useEffect(() => {
    if (!infoOpen) return;
    const onDocClick = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Element | null;
      if (!t || !t.closest) {
        setInfoOpen(false);
        return;
      }
      if (!t.closest('.medical-chat-info-wrap')) setInfoOpen(false);
    };
    const onScroll = () => setInfoOpen(false);
    document.addEventListener('click', onDocClick);
    document.addEventListener('touchstart', onDocClick, { passive: true });
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [infoOpen]);

  // Closing the panel also dismisses the info tooltip so it doesn't pop
  // back when the panel reopens.
  useEffect(() => {
    if (!open) setInfoOpen(false);
  }, [open]);

  const members = families.flatMap((f) =>
    f.members.map((mem) => ({
      ...mem,
      familyName: f.name,
    }))
  );

  const buildAssistantMeta = (res: MedicalChatResponse) => {
    const nameFor = (mid: number) => {
      const row = members.find((x) => x.id === mid);
      return row ? memberDisplayName(row) : `#${mid}`;
    };
    const parts: string[] = [];
    // In simplified mode we keep ONLY the legal disclaimer note (which is meaningful
    // to a non-tech user); everything else (dossier name, retrieval counts, agent
    // pass numbers, "Mod analiză extinsă", etc.) is power-user telemetry.
    if (isAdvanced) {
      if (res.resolved_member_id != null && !res.needs_member_clarification) {
        parts.push(`Dosar: ${nameFor(res.resolved_member_id)}`);
      }
      if (res.retrieved_count > 0) {
        parts.push(`Surse: ${res.retrieved_count} document(e) · pool: ${res.candidate_pool_size}`);
      }
      if (res.retrieval_escalation) {
        parts.push('Răspuns de clarificare (potrivire slabă)');
      }
      if (res.chat_mode === 'agentic') {
        parts.push('Mod analiză extinsă');
      }
      const am = res.agent_meta;
      if (am && typeof am.retrieval_passes === 'number') {
        parts.push(`Căutări: ${am.retrieval_passes}`);
      }
    }
    if (res.disclaimer_appended) {
      parts.push('Notă legală adăugată');
    }
    return parts.length ? parts.join(' · ') : undefined;
  };

  const buildHistoryPayload = (msgs: Msg[]): ChatHistoryTurn[] =>
    msgs
      .filter((m) => !m.memberPick)
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.text }));

  const appendAssistantFromResponse = (
    res: MedicalChatResponse,
    pick?: MemberCandidate[],
    opts?: { omitReasoning?: boolean }
  ) => {
    if (res.resolved_member_id != null) {
      setSessionMemberId(res.resolved_member_id);
    }
    setMessages((m) => [
      ...m,
      {
        role: 'assistant',
        text: res.answer,
        reasoning: opts?.omitReasoning ? undefined : (res.reasoning_content ?? undefined),
        citations: res.citations,
        meta: buildAssistantMeta(res),
        memberPick:
          res.needs_member_clarification && pick && pick.length > 0 ? pick : undefined,
      },
    ]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setError('');
    setMessages((m) => [...m, { role: 'user', text }]);
    setLoading(true);
    setAgenticStream(null);
    try {
      const historyPayload = buildHistoryPayload(messages);
      const docIdNum = routeDocumentId ? Number(routeDocumentId) : null;
      const docScoped = useCurrentDocument && typeof docIdNum === 'number' && Number.isFinite(docIdNum) && docIdNum > 0;
      // Simplified users still get to choose deep-analysis / focus from the UI;
      // only the `debug` telemetry channel is suppressed (it ends up in the
      // meta-info line which is hidden in simplified mode anyway).
      const streamOpts = {
        chat_mode: chatMode as 'standard' | 'agentic',
        history: historyPayload.length ? historyPayload : undefined,
        focus_mode: focusMode || null,
        document_id: docScoped ? docIdNum : null,
        debug: isAdvanced,
      };

      if (chatMode === 'agentic') {
        agenticStreamAbortRef.current?.abort();
        const ac = new AbortController();
        agenticStreamAbortRef.current = ac;
        setAgenticStream({ phaseLabel: '', liveThinking: '', liveAnswer: '' });
        await chatApi.medicalChatAgenticStream(
          sessionMemberId,
          text,
          'ro',
          streamOpts,
          (ev) => {
            if (ev.type === 'step') {
              setAgenticStream((s) =>
                s ? { ...s, phaseLabel: ev.label } : { phaseLabel: ev.label, liveThinking: '', liveAnswer: '' }
              );
            } else if (ev.type === 'thinking_delta') {
              setAgenticStream((s) =>
                s
                  ? { ...s, liveThinking: s.liveThinking + ev.text }
                  : { phaseLabel: '', liveThinking: ev.text, liveAnswer: '' }
              );
            } else if (ev.type === 'answer_delta') {
              setAgenticStream((s) =>
                s
                  ? { ...s, liveAnswer: s.liveAnswer + ev.text }
                  : { phaseLabel: '', liveThinking: '', liveAnswer: ev.text }
              );
            } else if (ev.type === 'done') {
              setAgenticStream(null);
              appendAssistantFromResponse(ev.response, ev.response.member_candidates ?? undefined, {
                omitReasoning: true,
              });
            } else if (ev.type === 'error') {
              setAgenticStream(null);
              setError(ev.detail);
              setMessages((m) => [...m, { role: 'assistant', text: `Eroare: ${ev.detail}` }]);
            }
          },
          ac.signal
        );
        if (agenticStreamAbortRef.current === ac) {
          agenticStreamAbortRef.current = null;
        }
      } else {
        const res = await chatApi.medicalChat(sessionMemberId, text, 'ro', streamOpts);
        appendAssistantFromResponse(res, res.member_candidates ?? undefined);
      }
    } catch (e: any) {
      const detail = e.response?.data?.detail || e.message || 'Eroare la asistent';
      setError(detail);
      setMessages((m) => [...m, { role: 'assistant', text: `Eroare: ${detail}` }]);
      setAgenticStream(null);
    } finally {
      setLoading(false);
    }
  };

  const getLastUserTextBefore = (messageIndex: number, all: Msg[]) => {
    for (let j = messageIndex - 1; j >= 0; j--) {
      if (all[j].role === 'user') return all[j].text;
    }
    return null;
  };

  const pickMemberAndContinue = async (clarificationMsgIndex: number, cand: MemberCandidate) => {
    const lastUser = getLastUserTextBefore(clarificationMsgIndex, messages);
    if (!lastUser || loading) return;
    const clearedForHistory = messages.map((msg, idx) =>
      idx === clarificationMsgIndex && msg.role === 'assistant'
        ? { ...msg, memberPick: undefined }
        : msg
    );
    const hist = buildHistoryPayload(clearedForHistory);
    setLoading(true);
    setError('');
    setAgenticStream(null);
    try {
      const streamOpts = {
        chat_mode: chatMode as 'standard' | 'agentic',
        history: hist.length ? hist : undefined,
        focus_mode: focusMode || null,
      };

      if (chatMode === 'agentic') {
        agenticStreamAbortRef.current?.abort();
        const ac = new AbortController();
        agenticStreamAbortRef.current = ac;
        setAgenticStream({ phaseLabel: '', liveThinking: '', liveAnswer: '' });
        await chatApi.medicalChatAgenticStream(
          cand.id,
          lastUser,
          'ro',
          streamOpts,
          (ev) => {
            if (ev.type === 'step') {
              setAgenticStream((s) =>
                s ? { ...s, phaseLabel: ev.label } : { phaseLabel: ev.label, liveThinking: '', liveAnswer: '' }
              );
            } else if (ev.type === 'thinking_delta') {
              setAgenticStream((s) =>
                s
                  ? { ...s, liveThinking: s.liveThinking + ev.text }
                  : { phaseLabel: '', liveThinking: ev.text, liveAnswer: '' }
              );
            } else if (ev.type === 'answer_delta') {
              setAgenticStream((s) =>
                s
                  ? { ...s, liveAnswer: s.liveAnswer + ev.text }
                  : { phaseLabel: '', liveThinking: '', liveAnswer: ev.text }
              );
            } else if (ev.type === 'done') {
              setAgenticStream(null);
              const res = ev.response;
              if (res.resolved_member_id != null) {
                setSessionMemberId(res.resolved_member_id);
              }
              setMessages((m) => {
                const cleared = m.map((msg, idx) =>
                  idx === clarificationMsgIndex && msg.role === 'assistant'
                    ? { ...msg, memberPick: undefined }
                    : msg
                );
                const nextPick =
                  res.needs_member_clarification && res.member_candidates && res.member_candidates.length > 0
                    ? res.member_candidates
                    : undefined;
                return [
                  ...cleared,
                  {
                    role: 'assistant' as const,
                    text: res.answer,
                    reasoning: undefined,
                    citations: res.citations,
                    meta: buildAssistantMeta(res),
                    memberPick: nextPick,
                  },
                ];
              });
            } else if (ev.type === 'error') {
              setAgenticStream(null);
              setError(ev.detail);
              setMessages((m) => [...m, { role: 'assistant', text: `Eroare: ${ev.detail}` }]);
            }
          },
          ac.signal
        );
        if (agenticStreamAbortRef.current === ac) {
          agenticStreamAbortRef.current = null;
        }
      } else {
        const res = await chatApi.medicalChat(cand.id, lastUser, 'ro', streamOpts);
        if (res.resolved_member_id != null) {
          setSessionMemberId(res.resolved_member_id);
        }
        setMessages((m) => {
          const cleared = m.map((msg, idx) =>
            idx === clarificationMsgIndex && msg.role === 'assistant'
              ? { ...msg, memberPick: undefined }
              : msg
          );
          const nextPick =
            res.needs_member_clarification && res.member_candidates && res.member_candidates.length > 0
              ? res.member_candidates
              : undefined;
          return [
            ...cleared,
            {
              role: 'assistant' as const,
              text: res.answer,
              reasoning: res.reasoning_content ?? undefined,
              citations: res.citations,
              meta: buildAssistantMeta(res),
              memberPick: nextPick,
            },
          ];
        });
      }
    } catch (e: any) {
      const detail = e.response?.data?.detail || e.message || 'Eroare la asistent';
      setError(detail);
      setMessages((m) => [...m, { role: 'assistant', text: `Eroare: ${detail}` }]);
      setAgenticStream(null);
    } finally {
      setLoading(false);
    }
  };

  const unwrapFenced = (content: string) => {
    if (!content) return content;
    return content.replace(/```(?:markdown)?\s*\n?([\s\S]*?)\n?```/gim, (_, c) => c || _).trim();
  };

  /** Post-process the assistant message text before rendering:
   *
   * 1.  Strip any trailing `SOURCES_USED: ...` line. The backend already
   *     does this for the committed message, but the live streaming
   *     `liveAnswer` hasn't been finalised yet — without this filter the
   *     user briefly sees the raw marker scroll past as the LLM types.
   * 2.  Rewrite `(Document N)` inline references into markdown links
   *     pointing at `/documents/N`. The agent prompt instructs the LLM
   *     to cite document_id as `(Document 12)` after each fact; this
   *     keeps the original parenthesised look while making the label a
   *     clickable link. The custom `a` renderer below upgrades these
   *     `/documents/...` links into React Router `<Link>`s so navigation
   *     stays within the SPA.
   */
  const processAssistantText = (text: string): string => {
    if (!text) return text;
    let cleaned = text.replace(
      /(?:\r?\n)+\s*SOURCES_USED\s*:\s*(?:none|[\d,\s]+)\s*$/i,
      ''
    );
    cleaned = cleaned.replace(
      /\((Document\s+(\d+))\)/gi,
      (_m: string, label: string, id: string) => `([${label}](/documents/${id}))`
    );
    return cleaned;
  };

  /** Custom markdown components: route `/documents/N` links through
   *  React Router so clicking an inline citation stays inside the SPA
   *  (and closes the chat panel, matching the existing footer
   *  citations behaviour). Non-internal links keep their default
   *  `<a>` rendering. */
  const mdComponents: Components = React.useMemo(
    () => ({
      a: ({ href, children, node: _node, ...rest }) => {
        if (typeof href === 'string' && href.startsWith('/documents/')) {
          return (
            <Link to={href} onClick={() => setOpen(false)}>
              {children}
            </Link>
          );
        }
        return (
          <a href={href} {...rest}>
            {children}
          </a>
        );
      },
    }),
    []
  );

  return (
    <div
      className={`medical-chat-widget${open ? ' medical-chat-widget--open' : ''}`}
      aria-live="polite"
    >
      {open && (
        <>
          <div
            className="medical-chat-widget-backdrop"
            role="presentation"
            onClick={() => setOpen(false)}
          />
          <div
            id="medical-chat-panel"
            ref={panelRef}
            className="medical-chat-widget-panel card"
            role="dialog"
            aria-label="Asistent dosar medical"
            aria-modal={mobileModal}
          >
          <header className="medical-chat-widget-header">
            <div className="medical-chat-widget-header-titles">
              <h2 className="medical-chat-widget-title">Asistent dosar medical</h2>
              <span className="medical-chat-info-wrap">
                <button
                  type="button"
                  className={`medical-chat-info-button${infoOpen ? ' is-open' : ''}`}
                  aria-describedby="medical-chat-info-tooltip"
                  aria-label="Cum funcționează selecția membrului"
                  aria-expanded={infoOpen}
                  onClick={(e) => {
                    e.stopPropagation();
                    setInfoOpen((v) => !v);
                  }}
                >
                  <InfoIcon />
                </button>
                <span
                  id="medical-chat-info-tooltip"
                  role="tooltip"
                  className="medical-chat-info-tooltip"
                >
                  Puteti numi membrul in intrebare (ex. „pentru Maria Ionescu”) sau selecta
                  din lista. Membrul ramane activ pana schimbati selectia sau numiti altcineva
                  in text.
                </span>
              </span>
            </div>
            <button
              type="button"
              className="medical-chat-widget-close"
              onClick={() => setOpen(false)}
              aria-label="Inchide asistentul"
            >
              <CloseIconSmall />
            </button>
          </header>

          {error && (
            <div className="alert alert-error" style={{ margin: '0 var(--sp-4) var(--sp-3)' }}>
              {error}
            </div>
          )}

          <div className="medical-chat-widget-toolbar">
            <label className="medical-chat-widget-toolbar-label" htmlFor="medical-chat-member-select">
              Membru
            </label>
            {members.length === 0 ? (
              <span className="medical-chat-widget-toolbar-empty">— adaugă din Familii</span>
            ) : (
              <span
                className={`medical-chat-member-chip-wrap${sessionMemberId == null ? ' medical-chat-member-chip-wrap--auto' : ''}`}
              >
                <select
                  id="medical-chat-member-select"
                  className="medical-chat-member-chip"
                  value={sessionMemberId == null ? '' : String(sessionMemberId)}
                  onChange={(e) => setSessionMemberId(e.target.value ? Number(e.target.value) : null)}
                  disabled={loadingFamilies || loading}
                  aria-label="Membru dosar pentru asistent"
                >
                  <option value="">Auto (din întrebare)</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {memberDisplayName(m)} ({m.familyName})
                    </option>
                  ))}
                </select>
              </span>
            )}
          </div>

          <div className="medical-chat-widget-messages">
            {loadingFamilies && (
              <div className="skeleton skeleton-line" style={{ width: '80%', height: 14 }} />
            )}
            {!loadingFamilies && messages.length === 0 && (
              <div className="medical-chat-widget-empty">
                <span className="medical-chat-widget-empty-dot" aria-hidden />
                <p className="medical-chat-widget-empty-text">
                  {isAdvanced
                    ? 'Întrebați despre dosar. Puteți menționa prenumele membrului; documentele trebuie să fie procesate (OCR complet).'
                    : 'Pune o întrebare despre documentele tale medicale — poți menționa prenumele unui membru.'}
                </p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  marginBottom: 'var(--sp-3)',
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '94%',
                    padding: 'var(--sp-2) var(--sp-3)',
                    borderRadius: 12,
                    background: msg.role === 'user' ? 'var(--color-primary-600)' : 'white',
                    color: msg.role === 'user' ? 'white' : 'var(--color-slate-800)',
                    boxShadow: 'var(--shadow-sm)',
                    fontSize: 'var(--text-sm)',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {/* Raw LLM chain-of-thought. Useful for power users debugging answers,
                      noise for everyone else. */}
                  {isAdvanced && msg.role === 'assistant' && msg.reasoning && (
                    <details className="medical-chat-reasoning">
                      <summary className="medical-chat-reasoning-summary">
                        Proces de gândire (model)
                      </summary>
                      <pre className="medical-chat-reasoning-pre">{msg.reasoning}</pre>
                    </details>
                  )}
                  <ReactMarkdown
                    className="md-content"
                    remarkPlugins={[remarkGfm]}
                    components={mdComponents}
                    urlTransform={(url, key) => {
                      if (key === 'src' && typeof url === 'string' && url.startsWith('data:image/')) return url;
                      return defaultUrlTransform(url);
                    }}
                  >
                    {processAssistantText(unwrapFenced(msg.text))}
                  </ReactMarkdown>

                  {msg.role === 'assistant' && msg.memberPick && msg.memberPick.length > 0 && (
                    <div className="medical-chat-quick-replies" role="group" aria-label="Alegeți un membru">
                      {msg.memberPick.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="medical-chat-quick-reply-btn"
                          disabled={loading}
                          onClick={() => pickMemberAndContinue(i, c)}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}

                  {msg.citations && msg.citations.length > 0 && (
                    <div
                      style={{
                        marginTop: 'var(--sp-2)',
                        paddingTop: 'var(--sp-2)',
                        borderTop: '1px solid var(--color-slate-200)',
                      }}
                    >
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-slate-500)', marginBottom: 6 }}>
                        Documente relevante
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {msg.citations.map((c) => (
                          <Link
                            key={c.document_id}
                            to={`/documents/${c.document_id}`}
                            onClick={() => setOpen(false)}
                            style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary-700)' }}
                          >
                            {isAdvanced && <>#{c.document_id} — </>}
                            {c.original_filename}
                            {isAdvanced && c.document_types.length > 0
                              ? ` · ${c.document_types.join(', ')}`
                              : ''}
                            {c.document_date ? ` · ${c.document_date.slice(0, 10)}` : ''}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  {msg.meta && (
                    <div style={{ marginTop: 6, fontSize: 'var(--text-xs)', opacity: 0.75 }}>{msg.meta}</div>
                  )}
                </div>
              </div>
            ))}
            {loading && agenticStream && (() => {
              // Friendly status copy + a re-mount key so the text fades in on each
              // phase swap (handled by CSS via `key`-driven mount).
              const friendly = friendlyPhaseLabel(agenticStream.phaseLabel);
              const hasAnswer = !!agenticStream.liveAnswer;
              const hasThinking = !!agenticStream.liveThinking;
              return (
                <div className="medical-chat-stream-panel">
                  {isAdvanced ? (
                    <>
                      <div className="medical-chat-stream-status">
                        <span
                          key={friendly}
                          className="medical-chat-stream-status-text"
                        >
                          {friendly}
                        </span>
                        <TypingDots />
                      </div>

                      {hasThinking && (
                        <div
                          className={`medical-chat-stream-thinking${thinkingExpanded ? ' is-expanded' : ''}`}
                        >
                          <button
                            type="button"
                            className="medical-chat-stream-thinking-toggle"
                            aria-expanded={thinkingExpanded}
                            aria-controls="medical-chat-stream-thinking-pre"
                            onClick={() => setThinkingExpanded((v) => !v)}
                          >
                            <ChevronRightIcon />
                            <span>{thinkingExpanded ? 'Ascunde gândirea' : 'Vezi gândirea…'}</span>
                          </button>
                          {thinkingExpanded && (
                            <pre
                              id="medical-chat-stream-thinking-pre"
                              className="medical-chat-stream-thinking-pre"
                            >
                              {agenticStream.liveThinking}
                            </pre>
                          )}
                        </div>
                      )}

                      {hasAnswer && (
                        <div className="medical-chat-stream-answer">
                          <ReactMarkdown
                            className="md-content"
                            remarkPlugins={[remarkGfm]}
                            components={mdComponents}
                            urlTransform={(url, key) => {
                              if (key === 'src' && typeof url === 'string' && url.startsWith('data:image/')) return url;
                              return defaultUrlTransform(url);
                            }}
                          >
                            {processAssistantText(unwrapFenced(agenticStream.liveAnswer))}
                          </ReactMarkdown>
                        </div>
                      )}
                    </>
                  ) : hasAnswer ? (
                    // Simplified mode: just the rolling answer with the same caret + fade.
                    <div className="medical-chat-stream-answer">
                      <ReactMarkdown
                        className="md-content"
                        remarkPlugins={[remarkGfm]}
                        components={mdComponents}
                        urlTransform={(url, key) => {
                          if (key === 'src' && typeof url === 'string' && url.startsWith('data:image/')) return url;
                          return defaultUrlTransform(url);
                        }}
                      >
                        {processAssistantText(unwrapFenced(agenticStream.liveAnswer))}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    // Simplified placeholder: friendly word + animated dots — alive,
                    // not a static "Se gândește…".
                    <div className="medical-chat-stream-status medical-chat-stream-status--solo">
                      <span className="medical-chat-stream-status-text">Se gândește</span>
                      <TypingDots />
                    </div>
                  )}
                </div>
              );
            })()}
            {loading && !agenticStream && (
              <div style={{ color: 'var(--color-slate-500)', fontSize: 'var(--text-sm)' }}>Se genereaza raspunsul...</div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="medical-chat-widget-footer">
            <div className="medical-chat-widget-options-row">
              {routeDocumentId && (
                <label className="medical-chat-widget-option">
                  <input
                    type="checkbox"
                    checked={useCurrentDocument}
                    onChange={(e) => setUseCurrentDocument(e.target.checked)}
                    disabled={loading || loadingFamilies}
                  />
                  <span>{isAdvanced ? 'Folosește documentul deschis' : 'Întreabă despre acest document'}</span>
                </label>
              )}
              <label className="medical-chat-focus">
                <span className="medical-chat-focus-label">{isAdvanced ? 'Focalizare' : 'Caută în'}</span>
                <span className="medical-chat-focus-wrap">
                  <select
                    className="medical-chat-focus-select"
                    value={focusMode}
                    onChange={(e) => setFocusMode(e.target.value)}
                    disabled={loading || loadingFamilies}
                    aria-label="Focalizare tip document"
                  >
                    <option value="">{isAdvanced ? 'Automat' : 'Toate documentele'}</option>
                    {focusModes.map((fm) => (
                      <option key={fm.key} value={fm.key}>
                        {fm.label_ro}
                      </option>
                    ))}
                  </select>
                </span>
              </label>
            </div>
            <div className="medical-chat-composer">
              <textarea
                className="medical-chat-input"
                rows={2}
                placeholder="Ex: Pentru Maria Ionescu, ultimele CT? / Valori leucocite la Ion?"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                disabled={loading || loadingFamilies}
                aria-label="Mesaj pentru asistent"
              />
              <button
                type="button"
                className="medical-chat-send"
                onClick={send}
                disabled={loading || loadingFamilies || !input.trim()}
                aria-label="Trimite mesajul"
                title="Trimite"
              >
                <SendIcon />
              </button>
            </div>
            <p className="medical-chat-composer-hint">Enter trimite · Shift+Enter linie nouă</p>
          </div>
        </div>
        </>
      )}

      <button
        type="button"
        className={`medical-chat-widget-fab${open ? ' medical-chat-widget-fab-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="medical-chat-panel"
        aria-label={open ? 'Inchide asistentul medical' : 'Deschide asistentul medical'}
      >
        {open ? <CloseIcon /> : <ChatBubbleIcon />}
      </button>
    </div>
  );
};

export default MedicalChatBubble;
